import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, ShieldCheck } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useAdminData } from "../useAdminData";
import { Panel, Skeleton, EmptyState, ErrorState, Button, PageHeader, MetricStrip, KpiCard } from "../Primitives";
import UserDialog from "./users/UserDialog";
import InviteLink from "./users/InviteLink";
import UserRow from "./users/UserRow";
import AuditLog from "./users/AuditLog";
import { ROLE_OPTIONS, STATUS } from "./users/roles";

function useDebounced(v, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

function Filters({ q, setQ, status, setStatus, role, setRole }) {
  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 mb-4" data-testid="users-filters">
      <div className="relative col-span-2 sm:flex-1 sm:min-w-[220px] sm:max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted2)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail..." aria-label="Buscar usuários"
          className="mia-input !pl-9 !h-8 !py-0" data-testid="users-search" />
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" data-testid="users-filter-status" className="mia-input !h-8 !py-0 sm:!w-auto">
        <option value="">Status: todos</option>
        {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Permissão" data-testid="users-filter-role" className="mia-input !h-8 !py-0 sm:!w-auto">
        <option value="">Permissão: todas</option>
        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </div>
  );
}

export default function Users() {
  const { checkSession } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState(null);
  const [tick, setTick] = useState(0);
  const dq = useDebounced(q);
  const { data, loading, error, reload } = useAdminData(
    (a) => a.get("/admin/users", { params: { q: dq || undefined, status: status || undefined, role: role || undefined } }).then((r) => r.data),
    [dq, status, role]);

  const changed = async (selfRevoked) => {
    if (selfRevoked) { await checkSession(); navigate("/admin/login", { replace: true }); return; }
    reload(); setTick((t) => t + 1);
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Usuários e acessos" meta="Contas da equipe com acesso ao painel de analytics. Nenhum perfil pode editar o conteúdo do site público.">
        <Button onClick={() => setCreating(true)} data-testid="users-new-btn"><UserPlus size={14} /> Novo acesso</Button>
      </PageHeader>

      {data && (
        <MetricStrip cols={3} className="mb-4 !grid-cols-3">
          <KpiCard label="Ativas" hint="Contas" value={data.counts.active} testid="users-count-active" />
          <KpiCard label="Pendentes" hint="Convites" value={data.counts.invited} testid="users-count-invited" />
          <KpiCard label="Inativas" hint="Desativadas" value={data.counts.disabled} testid="users-count-disabled" />
        </MetricStrip>
      )}

      <Filters {...{ q, setQ, status, setStatus, role, setRole }} />
      {error && <ErrorState message={error} onRetry={reload} />}

      <Panel className="mb-4" testid="users-panel">
        {loading && !data ? <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-11" />)}</div>
          : !data?.users.length ? <EmptyState title="Nenhum usuário encontrado" description="Ajuste a busca ou os filtros." />
          : (
            <div className="overflow-x-auto mia-scroll">
              <table className="ops-table ops-stack" data-testid="users-table">
                <thead><tr><th>Usuário</th><th>Nível de acesso</th><th>Status</th><th>Criado em</th><th>Último acesso</th><th className="!text-right">Ações</th></tr></thead>
                <tbody>
                  {data.users.map((u) => <UserRow key={u.id} u={u} isMe={u.id === data.me} onChanged={changed} onInvite={(inv, email) => setInvite({ ...inv, email })} />)}
                </tbody>
              </table>
            </div>
          )}
        <p className="px-4 py-3 ops-help border-t border-[var(--border)] flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-[var(--accent-ink)]" /> Permissões verificadas no servidor. A conta principal do proprietário e a sua própria conta não podem ser rebaixadas, desativadas ou excluídas.
        </p>
      </Panel>

      <AuditLog refreshKey={tick} />

      {creating && <UserDialog onClose={() => setCreating(false)} onCreated={(d) => { setCreating(false); setInvite({ ...d, email: d.user.email }); changed(); }} />}
      {invite && <InviteLink invite={invite} email={invite.email} onClose={() => setInvite(null)} />}
    </div>
  );
}
